import { useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Calendar, Trash2, Save, LogOut, History, ChevronDown, ChevronUp, Clock, Image, Upload, CheckCircle, Camera, ImageIcon, Pencil, Search, Receipt, User, CloudOff, MapPin } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { NumericInput } from "../components/ui/numeric-input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import EmployeePickerDialog from "../components/foreman/EmployeePickerDialog";
import { fuzzyMatch } from "../lib/searchUtils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { sortStrings } from "../lib/sortUtils";
import { DailyEntryAlert } from "../components/MissingReportsAlert";
import ForemanDigestBanner from "../components/ForemanDigestBanner";
import ProfilePage from "./ProfilePage";
import NotificationBell from "../components/NotificationBell";
import ForemanTasks from "../components/ForemanTasks";
import { useDailyEntry } from "../hooks/useDailyEntry";
import JobQuickActions from "../components/foreman/JobQuickActions";

const DailyEntryPage = () => {
  const inLayout = !useLocation().pathname.startsWith("/foreman"); // standalone foreman route has no sidebar/bell
  const {
    user, jobs, loading, submitting, showHistory, editingEntryId,
    pendingCount, nearbyJobs,
    employeesByRole, previousEntries, materials,
    selectedDate, selectedJob, notes, employeeEntries,
    photoJob, uploadMode, uploading, uploadConfirmOpen, uploadedPhotoCount,
    photoDescription, pendingFiles, descriptionDialogOpen,
    materialPickerOpen, selectedCategory, materialCategories,
    setShowHistory, setSelectedDate, setSelectedJob, setNotes,
    setPhotoJob, setUploadMode, setUploadConfirmOpen, setPhotoDescription,
    setDescriptionDialogOpen, setPendingFiles, setMaterialPickerOpen,
    setSelectedCategory,
    updateEmployeeEntry, openMaterialPicker, selectMaterial,
    getMaterialsInCategory, getSelectedMaterialName, updateMaterial,
    handleSubmit, handleEditEntry, handleCancelEdit, handleDeleteEntry,
    handlePhotoSelect, handlePhotoUpload, formatDate, getJobName,
    logout,
  } = useDailyEntry();

  const [materialSearch, setMaterialSearch] = useState("");
  const [showProfile, setShowProfile] = useState(false);
  const [activeTab, setActiveTab] = useState("hours");

  // Multi-word search using shared utility
  const searchMaterials = (query) => {
    if (!query.trim()) return [];
    return materials
      .filter((m) => fuzzyMatch(query, m.name || ""))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  };

  const materialSearchResults = searchMaterials(materialSearch);

  // Pop-up employee picker (index of the employee card being edited, or null)
  const [employeePickerIndex, setEmployeePickerIndex] = useState(null);
  const employeeName = (id) => Object.values(employeesByRole).flat().find((e) => e.id === id)?.name;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className={`${inLayout ? "pb-10" : "min-h-screen pb-10 md:pb-40"} bg-background`} data-testid="daily-entry-page">
      {/* Desktop header inside the main app layout: same fixed title row as every other page */}
      {inLayout && (
        <div className="page-header hidden md:block" data-testid="daily-entry-page-header">
          <div className="no-title-mask flex items-center gap-6 max-w-4xl mr-[8.5rem] lg:mr-32">
          <h1 className="text-4xl font-bold tracking-tight text-foreground shrink-0">Daily Entry</h1>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {activeTab === "hours" && editingEntryId && (
              <Button variant="outline" className="h-9 lg:h-11 px-3 lg:px-8 text-sm lg:text-base" onClick={handleCancelEdit} data-testid="cancel-edit-btn">Cancel Edit</Button>
            )}
            {activeTab === "hours" && (
              <Button onClick={handleSubmit} disabled={submitting || !selectedJob} variant="secondary" className="flex-1 min-w-0 h-9 lg:h-11 px-3 lg:px-8 text-sm lg:text-base font-semibold enabled:bg-green-600 enabled:text-white enabled:hover:bg-green-700" data-testid="submit-entry-btn">
                <Save className="w-4 h-4 lg:w-5 lg:h-5 mr-2 shrink-0" />
                <span className="truncate">
                  {submitting ? (editingEntryId ? "Updating..." : "Submitting...") : (editingEntryId ? "Update Entry" : <><span className="lg:hidden">Submit</span><span className="hidden lg:inline">Submit Daily Entry</span></>)}
                </span>
              </Button>
            )}
          </div>
          </div>
        </div>
      )}
      {/* Mobile / standalone foreman header */}
      <header className={`${inLayout ? "md:hidden fixed left-0 right-0 z-20 " : "sticky z-50 "}top-0 bg-primary text-primary-foreground px-4 py-3 shadow-lg`} data-page-header={inLayout ? "dark" : undefined}>
        <div className={`flex items-center justify-between ${inLayout ? "min-h-[60px] pl-10" : ""}`}>
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="V2R Masonry Systems" className="w-8 h-8 rounded-sm object-contain" />
            <div>
              <p className="font-bold text-lg leading-tight whitespace-nowrap">Daily Entry</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!inLayout && <ForemanTasks />}
            {!inLayout && <NotificationBell />}
            {!inLayout && <button
              type="button"
              onClick={() => setShowProfile(true)}
              className="flex items-center gap-1.5 text-sm opacity-90 hover:opacity-100 transition-opacity"
              data-testid="foreman-profile-btn"
            >
              <User className="w-4 h-4" />
              <span>{user?.name}</span>
            </button>}
            {!inLayout && <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              className="text-primary-foreground hover:bg-primary/80"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>}
          </div>
        </div>
        {/* Mobile-only static submit bar (desktop keeps the fixed bottom bar) */}
        {activeTab === "hours" && (
          <div className="md:hidden flex gap-2 pt-3" data-testid="mobile-submit-bar">
            {editingEntryId && (
              <Button variant="outline" onClick={handleCancelEdit} className="h-[54px] px-4 bg-transparent border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10" data-testid="cancel-edit-btn-mobile">
                Cancel
              </Button>
            )}
            <Button
              onClick={handleSubmit}
              disabled={submitting || !selectedJob}
              variant="secondary"
              className="flex-1 h-[54px] text-xl font-bold enabled:bg-green-600 enabled:text-white enabled:hover:bg-green-700"
              data-testid="submit-entry-btn-mobile"
            >
              <Save className="w-6 h-6 mr-2" />
              {submitting ? (editingEntryId ? "Updating..." : "Submitting...") : (editingEntryId ? "Update Entry" : "Submit Daily Entry")}
            </Button>
          </div>
        )}
      </header>

      <Dialog open={showProfile} onOpenChange={setShowProfile}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="foreman-profile-dialog"
        >
          <DialogHeader>
            <DialogTitle className="sr-only">My Profile</DialogTitle>
          </DialogHeader>
          {showProfile && <ProfilePage />}
        </DialogContent>
      </Dialog>

      <div className={`p-4 max-w-lg mx-auto ${inLayout ? "md:p-0 md:mx-0 md:max-w-4xl" : ""}`}>
        <ForemanDigestBanner />
        <DailyEntryAlert jobs={jobs} />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-2 h-auto">
            <TabsTrigger value="hours" className="text-sm sm:text-base py-2 px-1 sm:px-3 min-w-0" data-testid="hours-tab">
              <Clock className="w-[18px] h-[18px] mr-1 sm:mr-2 shrink-0" />
              <span className="truncate">Enter Hours</span>
            </TabsTrigger>
            <TabsTrigger value="photos" className="text-sm sm:text-base py-2 px-1 sm:px-3 min-w-0" data-testid="photos-tab">
              <Image className="w-[18px] h-[18px] mr-1 sm:mr-2 shrink-0" />
              <span className="truncate">Photos/Receipts</span>
            </TabsTrigger>
          </TabsList>

          {/* Hours Entry Tab */}
          <TabsContent value="hours" className="space-y-4">
            {/* Previous Entries Section */}
            <Card>
              <CardHeader
                className="py-3 cursor-pointer"
                onClick={() => setShowHistory(!showHistory)}
              >
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <History className="w-4 h-4" />
                    Edit/Delete Previous Entries ({previousEntries.length})
                  </CardTitle>
                  {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </CardHeader>
              {showHistory && (
                <CardContent className="pt-0">
                  {previousEntries.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4 text-sm">No previous entries</p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {previousEntries.slice(0, 10).map((entry) => (
                        <div
                          key={entry.id}
                          className={`flex items-center justify-between p-3 rounded-lg ${editingEntryId === entry.id ? 'bg-primary/10 border-2 border-primary' : 'bg-muted/30'}`}
                          data-testid={`entry-${entry.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{entry.job_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatDate(entry.date)} • {entry.employees?.length || 0} employees
                            </p>
                            {entry.notes && (
                              <p className="text-xs text-muted-foreground truncate mt-1">{entry.notes}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-primary"
                              onClick={() => handleEditEntry(entry)}
                              data-testid={`edit-entry-${entry.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive"
                                  data-testid={`delete-entry-${entry.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Entry?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will remove this daily entry and revert the labor/material costs from the job. This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteEntry(entry.id)}
                                    className="bg-destructive text-destructive-foreground"
                                  >
                                    Delete
                                  </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>

            {/* Offline queue / location hints */}
            {pendingCount > 0 && (
              <div className="flex items-start gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm" data-testid="pending-entries-banner">
                <CloudOff className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{pendingCount} daily {pendingCount === 1 ? "entry is" : "entries are"} saved on this phone and will send automatically once you have signal.</span>
              </div>
            )}
            {nearbyJobs.length > 0 && (
              <div className="flex items-start gap-2 p-3 rounded-lg border border-sky-200 bg-sky-50 text-sky-800 text-sm" data-testid="nearby-jobs-hint">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                <span>You're at <strong>{nearbyJobs.map((j) => j.name).join(", ")}</strong>{nearbyJobs.length === 1 ? " — job pre-selected." : "."}</span>
              </div>
            )}

            {/* Date Selection */}
            <Card>
              <CardContent className="pt-4">
                <div className="form-group">
                  <Label className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Date
                  </Label>
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    max={new Date().toISOString().split("T")[0]}
                    className="text-lg w-full min-w-0 max-w-full block"
                    data-testid="date-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Job Selection */}
            <Card>
              <CardContent className="pt-4">
                <div className="form-group">
                  <Label>Select Job</Label>
                  <Select value={selectedJob} onValueChange={setSelectedJob}>
                    <SelectTrigger className="text-lg" data-testid="job-select">
                      <SelectValue placeholder="Choose an active job..." />
                    </SelectTrigger>
                    <SelectContent>
                      {[...jobs].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((job) => (
                        <SelectItem key={job.id} value={job.id}>
                          {job.name} - {job.client_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {selectedJob && <div className="mt-3"><JobQuickActions job={jobs.find((j) => j.id === selectedJob)} /></div>}

                <div className="form-group mt-4">
                  <Label>Notes ({notes.length}/200)</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value.slice(0, 200))}
                    placeholder="Add any notes about the day's work..."
                    rows={2}
                    className="resize-none"
                    data-testid="notes-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Employee Entries */}
            <h2 className="text-lg font-semibold text-foreground pt-2">Employee Time & Materials</h2>

            {employeeEntries.map((entry, empIndex) => (
              <Card key={entry._uid || `emp-${empIndex}`} className="overflow-hidden">
                <CardHeader className="py-3 px-4 bg-muted/50">
                  <CardTitle className="text-base font-bold">
                    Employee {empIndex + 1}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-3">
                  <div className="flex gap-3">
                    <div className="form-group flex-[3]">
                      <Button
                        type="button"
                        variant="outline"
                        className={`w-full justify-start font-normal ${entry.employee_id ? "text-foreground" : "text-muted-foreground"}`}
                        onClick={() => setEmployeePickerIndex(empIndex)}
                        data-testid={`employee-select-${empIndex}`}
                      >
                        <span className="truncate">{employeeName(entry.employee_id) || "Select Employee"}</span>
                      </Button>
                    </div>
                    <div className="form-group flex-1">
                      <NumericInput
                        decimals={2}
                        min={0}
                        max={24}
                        value={entry.hours}
                        onChange={(e) => updateEmployeeEntry(empIndex, "hours", e.target.value)}
                        placeholder="Hrs"
                        data-testid={`hours-input-${empIndex}`}
                      />
                    </div>
                  </div>

                  <div className="space-y-2 pt-2 border-t border-border">
                    <Label className="text-xs text-muted-foreground">Materials Installed</Label>
                    {entry.materials.map((mat, matIndex) => (
                      <div key={mat._uid || `mat-${empIndex}-${matIndex}`} className="grid grid-cols-2 gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          className={`text-sm h-9 justify-start font-normal ${mat.material_id ? "text-foreground" : "text-muted-foreground"}`}
                          onClick={() => openMaterialPicker(empIndex, matIndex)}
                          data-testid={`material-btn-${empIndex}-${matIndex}`}
                        >
                          {mat.material_id ? getSelectedMaterialName(mat.material_id) : "Select Material..."}
                        </Button>
                        <NumericInput
                          decimals={2}
                          min={0}
                          value={mat.quantity}
                          onChange={(e) => updateMaterial(empIndex, matIndex, "quantity", e.target.value)}
                          placeholder="Qty"
                          className="text-sm"
                          data-testid={`quantity-input-${empIndex}-${matIndex}`}
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Fixed Submit Button for Hours (standalone desktop only; inside the app it lives in the page header) */}
            {!inLayout && (
            <div className="hidden md:block fixed bottom-0 left-0 right-0 bg-background border-t border-border shadow-lg z-40">
              <div className="absolute -top-6 left-0 right-0 h-6 bg-gradient-to-t from-background to-transparent pointer-events-none"></div>
              <div className="p-4 pb-16">
                {editingEntryId && (
                  <Button
                    variant="outline"
                    onClick={handleCancelEdit}
                    className="w-full mb-2"
                    data-testid="cancel-edit-btn"
                  >
                    Cancel Edit
                  </Button>
                )}
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || !selectedJob}
                  className="w-full h-16 text-2xl font-bold enabled:bg-green-600 enabled:hover:bg-green-700"
                  data-testid="submit-entry-btn"
                >
                  <Save className="w-7 h-7 mr-3" />
                  {submitting ? (editingEntryId ? "Updating..." : "Submitting...") : (editingEntryId ? "Update Entry" : "Submit Daily Entry")}
                </Button>
              </div>
            </div>
            )}
          </TabsContent>

          {/* Photos Upload Tab */}
          <TabsContent value="photos" className="space-y-4 pb-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {uploadMode === "receipt" ? "Upload Receipt" : "Upload Job Photos"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Photo / Receipt toggle */}
                <div className="grid grid-cols-2 gap-2 p-1 bg-muted rounded-lg" data-testid="upload-mode-toggle">
                  <Button
                    type="button"
                    variant={uploadMode === "photo" ? "default" : "ghost"}
                    className="h-10"
                    onClick={() => setUploadMode("photo")}
                    data-testid="upload-mode-photo-btn"
                  >
                    <Image className="w-4 h-4 mr-2" />
                    Photo
                  </Button>
                  <Button
                    type="button"
                    variant={uploadMode === "receipt" ? "default" : "ghost"}
                    className="h-10"
                    onClick={() => setUploadMode("receipt")}
                    data-testid="upload-mode-receipt-btn"
                  >
                    <Receipt className="w-4 h-4 mr-2" />
                    Receipt
                  </Button>
                </div>

                {uploadMode === "receipt" && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="receipt-reconcile-notice">
                    Receipts will be pre-assigned to the selected job and held for office reconciliation before the cost is applied.
                  </div>
                )}

                <div className="form-group">
                  <Label>Select Job First</Label>
                  <Select value={photoJob} onValueChange={setPhotoJob}>
                    <SelectTrigger data-testid="photo-job-select">
                      <SelectValue placeholder={`Choose a job for ${uploadMode === "receipt" ? "this receipt" : "photos"}...`} />
                    </SelectTrigger>
                    <SelectContent>
                      {jobs.map((job) => (
                        <SelectItem key={job.id} value={job.id}>
                          {job.id === 'system-office-job' ? job.name : `${job.name} - ${job.client_name}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {photoJob ? (
                  <div className="space-y-4">
                    <JobQuickActions job={jobs.find((j) => j.id === photoJob)} />
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handlePhotoSelect}
                      className="hidden"
                      id="photo-camera"
                      disabled={uploading}
                    />
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoSelect}
                      className="hidden"
                      id="photo-gallery"
                      disabled={uploading}
                    />

                    <label htmlFor="photo-camera" className="block cursor-pointer">
                      <div className="border-2 border-dashed border-primary rounded-lg p-6 text-center bg-primary/5 hover:bg-primary/10 transition-colors">
                        <Camera className="w-10 h-10 text-primary mx-auto mb-3" />
                        <p className="text-lg font-medium text-primary">
                          {uploading ? "Uploading..." : (uploadMode === "receipt" ? "Take Receipt Photo" : "Take Photo")}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Open camera to capture
                        </p>
                      </div>
                    </label>

                    <label htmlFor="photo-gallery" className="block cursor-pointer">
                      <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:bg-muted/50 transition-colors">
                        <ImageIcon className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                        <p className="text-lg font-medium">
                          {uploading ? "Uploading..." : "Choose from Gallery"}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {uploadMode === "receipt" ? "Select a receipt image" : "Select multiple photos at once"}
                        </p>
                      </div>
                    </label>

                    <p className="text-sm text-muted-foreground text-center">
                      Uploading to: <span className="font-medium">{getJobName(photoJob)}</span>
                    </p>
                    <p className="text-xs text-muted-foreground text-center">
                      JPG, PNG, GIF, WebP • Max 10MB per {uploadMode === "receipt" ? "receipt" : "photo"}
                    </p>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-border rounded-lg p-8 text-center bg-muted/30">
                    <Image className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                    <p className="text-muted-foreground">
                      Please select a job above to upload {uploadMode === "receipt" ? "a receipt" : "photos"}
                    </p>
                  </div>
                )}

                <p className="text-xs text-muted-foreground text-center">
                  {uploadMode === "receipt"
                    ? "Receipt amount will be auto-detected and applied after office reconciliation"
                    : "Photos are automatically date-stamped when uploaded"}
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Photo Description Dialog */}
      <Dialog open={descriptionDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setPendingFiles([]);
          setPhotoDescription("");
        }
        setDescriptionDialogOpen(open);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Add Description
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              {pendingFiles.length === 1
                ? "Photo ready to upload"
                : `${pendingFiles.length} photos ready to upload`}
            </p>
            <div className="space-y-2">
              <Label htmlFor="photo-description">Description (optional)</Label>
              <Textarea
                id="photo-description"
                value={photoDescription}
                onChange={(e) => setPhotoDescription(e.target.value)}
                placeholder="Add a description for the photo(s)..."
                className="w-full resize-none"
                rows={3}
                data-testid="photo-description-input"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setDescriptionDialogOpen(false);
                setPendingFiles([]);
                setPhotoDescription("");
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePhotoUpload}
              disabled={uploading}
              className="flex-1"
              data-testid="upload-photo-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? "Uploading..." : "Upload"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Confirmation Dialog */}
      <Dialog open={uploadConfirmOpen} onOpenChange={setUploadConfirmOpen}>
        <DialogContent className="text-center">
          <DialogHeader>
            <DialogTitle className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              {uploadMode === "receipt"
                ? "Receipt Uploaded — Pending Office Reconciliation"
                : (uploadedPhotoCount === 1 ? "Photo Uploaded Successfully!" : `${uploadedPhotoCount} Photos Uploaded!`)}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-muted-foreground mb-2">
              {uploadMode === "receipt" ? (
                <>Pre-assigned to <span className="font-medium">{getJobName(photoJob)}</span>. Office will reconcile before it hits the job.</>
              ) : (
                <><span className="font-medium">{uploadedPhotoCount} photo{uploadedPhotoCount > 1 ? 's' : ''}</span> added to job</>
              )}
            </p>
            {uploadMode !== "receipt" && (
              <p className="text-sm text-muted-foreground">
                Uploaded to: <span className="font-medium">{getJobName(photoJob)}</span>
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              {new Date().toLocaleString()}
            </p>
          </div>
          <Button onClick={() => setUploadConfirmOpen(false)} className="w-full">
            Done
          </Button>
        </DialogContent>
      </Dialog>

      {/* Material Picker Dialog */}
      <EmployeePickerDialog
        open={employeePickerIndex !== null}
        onOpenChange={(o) => { if (!o) setEmployeePickerIndex(null); }}
        employeesByRole={employeesByRole}
        value={employeePickerIndex !== null ? employeeEntries[employeePickerIndex]?.employee_id : undefined}
        onSelect={(id) => updateEmployeeEntry(employeePickerIndex, "employee_id", id)}
        testIdPrefix={`employee-select-${employeePickerIndex ?? 0}`}
      />

      <Dialog open={materialPickerOpen} onOpenChange={(open) => { setMaterialPickerOpen(open); if (!open) { setMaterialSearch(""); setSelectedCategory(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Select Material</DialogTitle>
          </DialogHeader>

          {/* Search bar - always visible */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search materials..."
              value={materialSearch}
              onChange={(e) => { setMaterialSearch(e.target.value); if (e.target.value) setSelectedCategory(""); }}
              className="pl-9"
              autoFocus
              data-testid="material-search-input"
            />
          </div>

          {/* Search results */}
          {materialSearch.trim() ? (
            <div className="grid gap-2 max-h-[50vh] overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>
              {materialSearchResults.length > 0 ? (
                materialSearchResults.map((material) => (
                  <Button
                    key={material.id}
                    variant="outline"
                    className="w-full justify-between h-auto py-3 text-left"
                    onClick={() => { selectMaterial(material.id); setMaterialSearch(""); }}
                    data-testid={`material-option-${material.id}`}
                  >
                    <div>
                      <p className="font-medium">{material.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {material.category} &middot; ${material.unit_price}/{material.unit}
                      </p>
                    </div>
                  </Button>
                ))
              ) : (
                <p className="text-center text-muted-foreground py-4">No materials matching "{materialSearch}"</p>
              )}
            </div>
          ) : !selectedCategory ? (
            /* Category browse */
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Or browse by category:</p>
              <div className="grid gap-2 max-h-[50vh] overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>
                {materialCategories.length > 0 ? (
                  sortStrings(materialCategories).map((category) => {
                    const count = getMaterialsInCategory(category).length;
                    return (
                      <Button
                        key={category}
                        variant="outline"
                        className="w-full justify-between h-12 text-left"
                        onClick={() => setSelectedCategory(category)}
                        data-testid={`category-btn-${category}`}
                      >
                        <span className="font-medium">{category}</span>
                        <span className="text-muted-foreground text-sm">{count} materials</span>
                      </Button>
                    );
                  })
                ) : (
                  <p className="text-center text-muted-foreground py-4">No categories available</p>
                )}
              </div>
            </div>
          ) : (
            /* Materials in selected category */
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedCategory("")}
                  className="text-muted-foreground"
                >
                  ← Back
                </Button>
                <span className="font-medium">{selectedCategory}</span>
              </div>
              <div className="grid gap-2 max-h-[50vh] overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>
                {getMaterialsInCategory(selectedCategory)
                  .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                  .map((material) => (
                  <Button
                    key={material.id}
                    variant="outline"
                    className="w-full justify-between h-auto py-3 text-left"
                    onClick={() => selectMaterial(material.id)}
                    data-testid={`material-option-${material.id}`}
                  >
                    <div>
                      <p className="font-medium">{material.name}</p>
                      <p className="text-xs text-muted-foreground">
                        ${material.unit_price}/{material.unit}
                      </p>
                    </div>
                  </Button>
                ))}
                {getMaterialsInCategory(selectedCategory).length === 0 && (
                  <p className="text-center text-muted-foreground py-4">No materials in this category</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DailyEntryPage;
